import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowLeft, FaShieldHalved } from "react-icons/fa6";
import { FiEdit, FiSearch } from "react-icons/fi";
import { MdOutlineAccountTree, MdOutlineChecklist } from "react-icons/md";
import { IoChevronDown } from "react-icons/io5";
import { BsCheckCircleFill, BsCircle } from "react-icons/bs";
import { FaBusinessTime } from "react-icons/fa";
import Handler from "../../Helpers/Handler";
import { formatDateTime, textCapitalize } from "../../Helpers/Utils";
import { Skeleton, StatusPill } from "../../Components/Needed";
import RoleFormModal from "./RoleFormModal";
import Timeline from "../../Components/Timeline";
import { listBackPath } from "../../Helpers/useListParams";

const RoleDetail = () => {
    const { roleId } = useParams();
    const navigate = useNavigate();
    const [role, setRole] = useState(null);
    const [catalog, setCatalog] = useState([]);
    const [shiftTypes, setShiftTypes] = useState([]);
    const [logs, setLogs] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [togglingStatus, setTogglingStatus] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [search, setSearch] = useState("");
    const [grantedOnly, setGrantedOnly] = useState(true);
    const [collapsed, setCollapsed] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        const [roleResponse, logResponse, departmentsResponse, catalogResponse, shiftResponse] = await Promise.all([
            Handler({ method: "GET", url: `/admin/staff/role/${roleId}` }),
            Handler({ method: "GET", url: `/admin/staff/role/${roleId}/log`, params: { limit: 20 } }),
            Handler({ method: "GET", url: "/admin/staff/department/active" }),
            Handler({ method: "GET", url: "/admin/staff/role/capabilities" }),
            Handler({ method: "GET", url: "/admin/staff/role/shift-types" }),
        ]);
        setRole(roleResponse?.success ? roleResponse?.data?.data : null);
        setLogs(logResponse?.success ? logResponse?.data?.data || [] : []);
        setDepartments(departmentsResponse?.success ? departmentsResponse?.data?.data || [] : []);
        setCatalog(catalogResponse?.success ? catalogResponse?.data?.data || [] : []);
        setShiftTypes(shiftResponse?.success ? shiftResponse?.data?.data || [] : []);
        setLoading(false);
    }, [roleId]);

    useEffect(() => { load(); }, [load]);

    const granted = useMemo(() => role?.permissions || [], [role]);

    const modules = useMemo(() => {
        const byModule = {};
        catalog.forEach((item) => {
            if (!byModule[item.module]) byModule[item.module] = [];
            byModule[item.module].push(item);
        });
        const orphans = (role?.permissions || []).filter((key) => !catalog.some((item) => item.key === key));
        if (orphans.length) {
            byModule.Unrecognised = orphans.map((key) => ({ key, label: key, module: "Unrecognised" }));
        }
        const term = search.trim().toLowerCase();
        return Object.keys(byModule)
            .map((name) => {
                const items = byModule[name];
                const visible = items.filter((item) => {
                    if (grantedOnly && !granted.includes(item.key)) return false;
                    if (!term) return true;
                    return item.label.toLowerCase().includes(term) || item.key.toLowerCase().includes(term) || name.toLowerCase().includes(term);
                });
                const grantedCount = items.filter((item) => granted.includes(item.key)).length;
                return { name, items, visible, grantedCount, total: items.length };
            })
            .filter((group) => group.visible.length);
    }, [catalog, role, granted, search, grantedOnly]);

    const totals = useMemo(() => {
        const dedupe = (values) => values.filter((value, index, all) => all.indexOf(value) === index);
        const allModules = dedupe(catalog.map((item) => item.module));
        const coveredModules = dedupe(catalog.filter((item) => granted.includes(item.key)).map((item) => item.module));
        return {
            granted: granted.length,
            available: catalog.length,
            modulesCovered: coveredModules.length,
            modulesTotal: allModules.length,
        };
    }, [catalog, granted]);

    const toggleStatus = async () => {
        setTogglingStatus(true);
        const response = await Handler({
            method: "patch",
            url: `/admin/staff/role/${roleId}/status`,
            data: { status: !role?.status },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Status updated");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update status"));
        }
        setTogglingStatus(false);
    };

    if (loading) {
        return (
            <div className="container-fluid px-0">
                <Skeleton height={140} className="mb-3 rounded-4" />
                <div className="row g-3">
                    <div className="col-12 col-lg-8"><Skeleton height={380} className="rounded-4" /></div>
                    <div className="col-12 col-lg-4"><Skeleton height={380} className="rounded-4" /></div>
                </div>
            </div>
        );
    }

    if (!role) {
        return (
            <div className="text-center py-5">
                <p className="text-muted mb-3">Role not found.</p>
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(listBackPath("/roles"))}>Back to roles</button>
            </div>
        );
    }

    const shiftLabel = shiftTypes.find((item) => item.value === role.shiftType)?.label;
    const coveragePct = totals.available ? Math.round((totals.granted / totals.available) * 100) : 0;

    return (
        <div className="container-fluid px-0">
            <button type="button" className="btn btn-link text-decoration-none px-0 mb-2 d-inline-flex align-items-center gap-2" onClick={() => navigate(listBackPath("/roles"))}>
                <FaArrowLeft /> Back to roles
            </button>

            {/* ---------- Identity ---------- */}
            <div className="role-hero card border-0 shadow-sm rounded-4 mb-3">
                <div className="card-body p-4">
                    <div className="d-flex flex-wrap align-items-start gap-3">
                        <div className="role-avatar flex-shrink-0">{(role.roleName || "?").charAt(0).toUpperCase()}</div>
                        <div className="flex-grow-1 min-w-0">
                            <h3 className="fw-bold text-capitalize mb-1">{role.roleName}</h3>
                            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                <span className="role-id-chip font-monospace">{role.roleId}</span>
                                <Link to={`/departments/${role.departmentId}`} className="role-dept-link text-capitalize">
                                    <MdOutlineAccountTree /> {role.departmentName || role.departmentId}
                                </Link>
                            </div>
                            <div className="d-flex flex-wrap align-items-center gap-2">
                                <StatusPill active={role.status} />
                                {shiftLabel || role.shiftType ? (
                                    <span className="role-chip"><FaBusinessTime /> {shiftLabel || role.shiftType}</span>
                                ) : (
                                    <span className="role-chip role-chip-muted"><FaBusinessTime /> No shift type</span>
                                )}
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 ms-auto">
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={toggleStatus} disabled={togglingStatus}>
                                {togglingStatus ? "Updating..." : role.status ? "Deactivate" : "Activate"}
                            </button>
                            <button type="button" className="btn btn-sm btn-primary d-inline-flex align-items-center gap-2" onClick={() => setShowEditModal(true)}>
                                <FiEdit /> Edit role
                            </button>
                        </div>
                    </div>

                    {/* ---------- At a glance ---------- */}
                    <div className="row g-3 mt-1">
                        <div className="col-6 col-lg-3">
                            <div className="role-stat">
                                <div className="role-stat-value">{totals.granted}<span className="role-stat-of">/ {totals.available}</span></div>
                                <div className="role-stat-label">Permissions granted</div>
                                <div className="role-cov-track mt-2"><div className="role-cov-fill" style={{ width: `${coveragePct}%` }} /></div>
                            </div>
                        </div>
                        <div className="col-6 col-lg-3">
                            <div className="role-stat">
                                <div className="role-stat-value">{totals.modulesCovered}<span className="role-stat-of">/ {totals.modulesTotal}</span></div>
                                <div className="role-stat-label">Modules with access</div>
                            </div>
                        </div>
                        <div className="col-6 col-lg-3">
                            <div className="role-stat">
                                <div className="role-stat-value">{role.tasks?.length || 0}</div>
                                <div className="role-stat-label">Assignable tasks</div>
                            </div>
                        </div>
                        <div className="col-6 col-lg-3">
                            <div className="role-stat">
                                <div className="role-stat-value role-stat-date">{formatDateTime(role.updatedAt)}</div>
                                <div className="role-stat-label">Last updated</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-3">
                {/* ---------- Permissions ---------- */}
                <div className="col-12 col-xl-8">
                    <div className="card border-0 shadow-sm rounded-4">
                        <div className="p-3 pb-0">
                            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                                <h5 className="fw-semibold d-flex align-items-center gap-2 mb-0">
                                    <FaShieldHalved className="text-primary" /> Permissions
                                </h5>
                                <span className="badge rounded-pill text-bg-light border ms-1">{totals.granted} granted</span>
                                <div className="ms-auto d-flex align-items-center gap-2">
                                    <div className="role-search">
                                        <FiSearch />
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Search permissions..."
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                    </div>
                                    <div className="btn-group btn-group-sm" role="group">
                                        <button type="button" className={`btn ${grantedOnly ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setGrantedOnly(true)}>Granted</button>
                                        <button type="button" className={`btn ${!grantedOnly ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setGrantedOnly(false)}>All</button>
                                    </div>
                                </div>
                            </div>
                            <hr className="m-0" />
                        </div>

                        <div className="card-body pt-3">
                            {modules.length ? modules.map((group) => {
                                const isCollapsed = collapsed[group.name];
                                const pct = group.total ? Math.round((group.grantedCount / group.total) * 100) : 0;
                                return (
                                    <div key={group.name} className="role-mod mb-3">
                                        <button
                                            type="button"
                                            className="role-mod-head"
                                            onClick={() => setCollapsed((prev) => ({ ...prev, [group.name]: !prev[group.name] }))}
                                            aria-expanded={!isCollapsed}
                                        >
                                            <IoChevronDown className={`role-mod-caret ${isCollapsed ? "is-collapsed" : ""}`} />
                                            <span className="role-mod-name">{group.name}</span>
                                            <span className={`role-mod-count ${group.grantedCount ? "is-on" : ""}`}>{group.grantedCount} / {group.total}</span>
                                            <span className="role-cov-track role-mod-track"><span className="role-cov-fill" style={{ width: `${pct}%` }} /></span>
                                        </button>
                                        {!isCollapsed ? (
                                            <div className="row g-2 mt-1">
                                                {group.visible.map((item) => {
                                                    const on = granted.includes(item.key);
                                                    return (
                                                        <div key={item.key} className="col-12 col-md-6">
                                                            <div className={`role-perm ${on ? "is-on" : ""}`}>
                                                                {on ? <BsCheckCircleFill className="role-perm-icon on" /> : <BsCircle className="role-perm-icon off" />}
                                                                <div className="min-w-0">
                                                                    <div className="role-perm-label">{item.label}</div>
                                                                    <div className="role-perm-key font-monospace">{item.key}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : ""}
                                    </div>
                                );
                            }) : (
                                <div className="text-center py-5">
                                    <FaShieldHalved className="text-muted mb-2" size={28} />
                                    <p className="text-muted mb-0">
                                        {search ? "No permissions match that search." : grantedOnly ? "No permissions granted to this role." : "No capabilities available."}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* ---------- Meta + activity ---------- */}
                <div className="col-12 col-xl-4">
                    <div className="card border-0 shadow-sm rounded-4 mb-3">
                        <div className="p-3"> {console.log(role, "role")}
                            <h5 className="fw-semibold d-flex align-items-center gap-2 mb-0">
                                <MdOutlineChecklist className="text-primary" /> Details
                            </h5>
                        </div>
                        <hr className="m-0" />
                        <div className="card-body">
                            {/* <div className="role-meta-row">
                                <span className="role-meta-label">Description</span>
                                <span className="role-meta-value">{role.description || "-"}</span>
                            </div> */}
                            <div className="role-meta-row">
                                <span className="role-meta-label">Created</span>
                                <span className="role-meta-value">{formatDateTime(role.createdAt)}</span>
                            </div>
                            <div className="role-meta-row align-items-start">
                                <span className="role-meta-label">Assignable tasks</span>
                                <span className="role-meta-value">
                                    {role.tasks?.length ? (
                                        <span className="d-flex flex-wrap gap-1">
                                            {role.tasks.map((task) => (
                                                <span key={task} className="role-task-chip text-capitalize">{task}</span>
                                            ))}
                                        </span>
                                    ) : "-"}
                                </span>
                            </div>
                        </div>
                    </div>
                    <Timeline userLog={logs} />
                </div>
            </div>

            {showEditModal ? (
                <RoleFormModal
                    mode="edit"
                    role={role}
                    departments={departments}
                    onClose={() => setShowEditModal(false)}
                    onSaved={() => { setShowEditModal(false); load(); }}
                />
            ) : ""}
        </div>
    );
};

export default RoleDetail;
